import React, { useState, useCallback, useEffect } from "react";

const HeadingDropdown: React.FC<{ editor: any }> = ({ editor }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentHeading, setCurrentHeading] = useState<{ level: number; label: string; size: string } | null>(null);
  const [isNormalText, setIsNormalText] = useState(true);
  
  const headings = [
    { level: 1, label: 'Heading 1', size: 'fs-18' },
    { level: 2, label: 'Heading 2', size: 'fs-16' },
    { level: 3, label: 'Heading 3', size: 'fs-14' },
    { level: 4, label: 'Heading 4', size: 'fs-12' },
    { level: 5, label: 'Heading 5', size: 'fs-10' },
    { level: 6, label: 'Heading 6', size: 'fs-10' },
  ];

  // Function to get current heading state
  const getCurrentHeadingState = useCallback(() => {
    if (!editor) return { heading: null, isNormal: true };

    // Check if paragraph is active (normal text)
    const isParagraph = editor.isActive('paragraph');
    
    // Check each heading level
    for (const heading of headings) {
      if (editor.isActive('heading', { level: heading.level })) {
        return { heading, isNormal: false };
      }
    }

    return { heading: null, isNormal: isParagraph };
  }, [editor, headings]);

  // Update current heading when editor changes
  useEffect(() => {
    const updateCurrentHeading = () => {
      const { heading, isNormal } = getCurrentHeadingState();
      setCurrentHeading(heading);
      setIsNormalText(isNormal);
    };

    if (editor) {
      editor.on('selectionUpdate', updateCurrentHeading);
      editor.on('transaction', updateCurrentHeading);
      
      // Initial update
      updateCurrentHeading();
    }

    return () => {
      if (editor) {
        editor.off('selectionUpdate', updateCurrentHeading);
        editor.off('transaction', updateCurrentHeading);
      }
    };
  }, [editor, getCurrentHeadingState]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (isOpen && !target.closest('.dropdown')) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleNormalText = () => {
    editor.chain().focus().setParagraph().run();
    setIsOpen(false);
  };

  const handleHeading = (level: number) => {
    editor.chain().focus().toggleHeading({ level }).run();
    setIsOpen(false);
  };
  
  return (
    <div className="dropdown heading-dropdown">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`
          btn btn-sm d-flex align-items-center px-3
          ${currentHeading || !isNormalText
            ? 'btn-secondary' 
            : 'btn-outline-secondary'
          }
        `}
        style={{ minWidth: '100px' }}
        title={`Text Format: ${currentHeading ? `Heading ${currentHeading.level}` : 'Normal'}`}
      >
        <span className="small fs-15">
          {currentHeading ? `H${currentHeading.level}` : 'Normal'}
        </span>
        <i className="ti ti-chevron-down ms-1 fs-12"></i>
      </button>
      
      {isOpen && (
        <ul className="dropdown-menu show position-absolute mt-1" style={{ minWidth: '160px', zIndex: 1000 }}>
          <li>
            <button
              onClick={handleNormalText}
              className={`dropdown-item ${isNormalText ? 'active' : ''}`}
            >
              Normal Text
            </button>
          </li>
          {headings.map((heading) => (
            <li key={heading.level}>
              <button
                onClick={() => handleHeading(heading.level)}
                className={`dropdown-item ${heading.size} fw-semibold ${
                  currentHeading?.level === heading.level ? 'active' : ''
                }`}
              >
                {heading.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default HeadingDropdown;
